import React, { useState, useEffect } from "react";
import { Formik, Field, Form } from "formik";
import {
    InputGroup,
    FieldWrappers,
    Label,
    SubmitBtn,
    SideCancelBtn,
    Wrapper,
    ScrollableContent,
    ActionSection,
    SelectionCard,
    CardWrapper,
} from "../Styles/InputStyles";
import { MdClose } from "react-icons/md";
import tw from "tailwind-styled-components";
import * as Yup from "yup";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";

const discountTypeOptions = [
    { value: 1, label: 'Flat' },
    { value: 2, label: 'Percentage' }
];

const discountPeriodOptions = Array.from({ length: 12 }, (_, i) => i + 1);

const AddDiscount = ({ addDiscountMutate, addDiscountLoading, handleClose, tenantId, monthlyRent }) => {

    const initialValues = {
        discountType: 1,
        discountAmount: "",
        discountPeriod: 1,
    };
    const [height, setHeight] = useState(window.innerHeight);

    useEffect(() => {
        const handleResize = () => {
            setHeight(window.innerHeight);
        };

        window.addEventListener('resize', handleResize);
        handleResize();

        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    const getValidationSchema = () => {
        return Yup.object().shape({
            discountType: Yup.string()
                .required("Discount type is required"),
            discountAmount: Yup.number()
                .min(1, "Discount amount must be greater than 0")
                .test(
                    'max-validation',
                    'Discount amount exceeds maximum allowed',
                    function (value) {
                        if (!value) return true;

                        const { discountType } = this.parent;

                        if (discountType === 1) {
                            const maxAmount = Math.min(monthlyRent);
                            return value <= maxAmount;
                        } else if (discountType === 2) {
                            return value <= 100;
                        }
                        return true;
                    }
                )
                .required("Discount amount is required"),
            discountPeriod: Yup.number()
                .min(1, "Discount period must be at least 1 month")
                .required("Discount period is required")
        });
    };

    const handleSubmit = (values) => {
        addDiscountMutate({
            discountType: values.discountType,
            discount: values.discountAmount,
            discountPeriod: values.discountPeriod,
            tenantId: tenantId,
            isDiscountFromFirstMonth: 1,
        });
    };

    return (
        <Container style={{ height: `${height}px`, overflow: 'auto', WebkitOverflowScrolling: 'touch' }}>
            <Header>
                <h1 className="text-white text-lg font-medium">Add Discount</h1>
                <button onClick={handleClose}>
                    <MdClose color="white" size={24} />
                </button>
            </Header>
            <div className="px-6 h-full flex flex-col justify-between">
                <Formik
                    initialValues={initialValues}
                    onSubmit={handleSubmit}
                    validationSchema={getValidationSchema()}
                    enableReinitialize
                >
                    {({ values, setFieldValue, errors, touched }) => (
                        <Form>
                            <ScrollableContent className="max-h-[calc(100dvh-140px)]">
                                <Wrapper>
                                    <InputGroup>
                                        <Label htmlFor="discountType">Discount Type *</Label>
                                        <CardWrapper>
                                            {discountTypeOptions.map((mode) => (
                                                <SelectionCard
                                                    key={mode.value}
                                                    type="button"
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        setFieldValue("discountType", mode.value);

                                                        if (values.discountAmount && (
                                                            (mode.value === 1 && values.discountAmount > monthlyRent) ||
                                                            (mode.value === 2 && values.discountAmount > 100)
                                                        )) {
                                                            setFieldValue("discountAmount", "");
                                                        }
                                                    }}
                                                    className={values.discountType === mode.value
                                                        ? "bg-[#26A69A] text-white"
                                                        : "bg-gray-100 text-gray-600"}
                                                >
                                                    <span className="text-sm">{mode.label}</span>
                                                </SelectionCard>
                                            ))}
                                        </CardWrapper>
                                        {touched.discountType && errors.discountType && (
                                            <p className="text-rose-600 text-sm">
                                                {errors.discountType}
                                            </p>
                                        )}
                                    </InputGroup>

                                    <InputGroup>
                                        <Label htmlFor="discountAmount">
                                            {values.discountType === 1
                                                ? `Discount Amount * (Max: ₹${Math.min(monthlyRent)})`
                                                : 'Discount Percentage * (Max: 100%)'}
                                        </Label>
                                        <FieldWrappers>
                                            <Field
                                                type="number"
                                                name="discountAmount"
                                                id="discountAmount"
                                                autoComplete="off"
                                                min="1"
                                                max={values.discountType === 1 ? Math.min(monthlyRent) : 100}
                                                placeholder={values.discountType === 1 ? "Amount" : "Percentage"}
                                            />
                                        </FieldWrappers>
                                        {errors.discountAmount && touched.discountAmount && (
                                            <ErrorMessage>{errors.discountAmount}</ErrorMessage>
                                        )}

                                    </InputGroup>

                                    <InputGroup>
                                        <Label htmlFor="discountPeriod">Discounted Rent Cycle *</Label>
                                        <Select
                                            value={values.discountPeriod || ""}
                                            onChange={(e) => setFieldValue("discountPeriod", e.target.value)}
                                            displayEmpty
                                            inputProps={{ 'aria-label': 'Select discount period' }}
                                            className="w-full px-2 h-10"
                                        >
                                            {discountPeriodOptions.map((month) => (
                                                <MenuItem key={month} value={month}>
                                                    {month} {month === 1 ? 'month' : 'months'}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                        {errors.discountPeriod && touched.discountPeriod && (
                                            <ErrorMessage>{errors.discountPeriod}</ErrorMessage>
                                        )}
                                    </InputGroup>

                                    <div className="mt-4 p-4 bg-gray-100 rounded-md text-sm text-gray-600">
                                        <p className="font-medium mb-2">Important Notes:</p>
                                        <ul className="list-disc pl-5 space-y-1">
                                            <li>Discounts are applicable only on full-month rent payments.</li>
                                            <li>Discounts do not apply to rents generated during:</li>
                                            <ul className="list-disc pl-5 mt-1">
                                                <li>Tenant eviction</li>
                                                <li>Room change</li>
                                                <li>Property change</li>
                                            </ul>
                                        </ul>
                                    </div>
                                </Wrapper>
                            </ScrollableContent>
                            <ActionSection>
                                <SideCancelBtn type="button" onClick={handleClose}>
                                    Cancel
                                </SideCancelBtn>
                                <SubmitBtn type="submit" disabled={addDiscountLoading}>
                                    {addDiscountLoading ? "Loading..." : "Add Discount"}
                                </SubmitBtn>
                            </ActionSection>
                        </Form>
                    )}
                </Formik>
            </div>
        </Container>
    );
};

const Container = tw.div`bg-white w-full h-full flex flex-col relative`;
const Header = tw.div`flex items-center justify-between bg-[#26A69A] py-4 px-4`;
const ErrorMessage = tw.div`text-red-500 text-xs mt-1`;

export default AddDiscount;